import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface SpotlightGuideProps {
  targetSelector: string; // CSS selector for the button to highlight
  onDismiss: () => void;
  message?: string;
}

/**
 * Quick & dirty spotlight guide that highlights a target element
 * with an overlay and tooltip
 */
export const SpotlightGuide: React.FC<SpotlightGuideProps> = ({
  targetSelector,
  onDismiss,
  message = 'Changed your mind? Try demo mode here!',
}) => {
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const handleDismiss = React.useCallback(() => {
    setIsVisible(false);

    // Gracefully remove magnifying effect before dismissing
    const target = document.querySelector(targetSelector);
    if (target instanceof HTMLElement) {
      // Reset transform smoothly
      target.style.transform = '';
      // Keep transition for smooth animation
      setTimeout(() => {
        target.style.transition = '';
        target.style.zIndex = '';
        target.style.position = '';
      }, 300);
    }

    setTimeout(onDismiss, 300); // Wait for fade-out animation
  }, [targetSelector, onDismiss]);

  useEffect(() => {
    // Find the target element
    const findTarget = () => {
      const target = document.querySelector(targetSelector);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);
        setIsVisible(true);

        // Add magnifying effect to the target element
        // Preserve existing transform if any (e.g., from hover effects)
        if (target instanceof HTMLElement) {
          const currentTransform = window.getComputedStyle(target).transform;
          const baseTransform =
            currentTransform !== 'none' ? currentTransform : '';
          target.style.transform = `${baseTransform} scale(1.05)`.trim();
          target.style.transition = 'transform 0.3s ease-out';
          target.style.zIndex = '9999';
          target.style.position = 'relative';
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(findTarget, 300);

    // Auto-dismiss after 10 seconds
    const autoDismiss = setTimeout(() => {
      handleDismiss();
    }, 10000);

    // Handle ESC key
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      clearTimeout(timer);
      clearTimeout(autoDismiss);
      window.removeEventListener('keydown', handleEsc);

      // Clean up magnifying effect gracefully
      const target = document.querySelector(targetSelector);
      if (target instanceof HTMLElement) {
        target.style.transform = '';
        // Delay removing other styles to allow smooth transition
        setTimeout(() => {
          if (target instanceof HTMLElement) {
            target.style.transition = '';
            target.style.zIndex = '';
            target.style.position = '';
          }
        }, 300);
      }
    };
  }, [targetSelector, handleDismiss]);

  if (!targetRect || !isVisible) return null;

  // Calculate cutout position with padding (vertical and horizontal)
  const paddingVertical = 8;
  const paddingHorizontal = 12;
  const cutout = {
    top: targetRect.top - paddingVertical,
    left: targetRect.left - paddingHorizontal,
    width: targetRect.width + paddingHorizontal * 2,
    height: targetRect.height + paddingVertical * 2,
  };

  // Smart tooltip positioning - adapt based on available space
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const tooltipWidth = 220;
  const tooltipHeight = 90;

  // Check if button is on the left side of screen (sidebar)
  const isLeftSide = cutout.left < viewportWidth / 2;

  // Default: position to the right of button, vertically centered
  let tooltipTop = cutout.top + cutout.height / 2 - tooltipHeight / 2;
  let tooltipLeft = cutout.left + cutout.width + 16;
  let arrowPosition: 'left' | 'top' | 'bottom' | 'right' = 'left';

  // If on left side and enough space to the right
  if (
    isLeftSide &&
    cutout.left + cutout.width + tooltipWidth + 24 > viewportWidth
  ) {
    // Not enough space to right, position above
    tooltipTop = cutout.top - tooltipHeight - 12;
    tooltipLeft = cutout.left + cutout.width / 2 - tooltipWidth / 2;
    arrowPosition = 'bottom';
  } else if (!isLeftSide) {
    // Button on right side, position to the left
    tooltipLeft = cutout.left - tooltipWidth - 16;
    tooltipTop = cutout.top + cutout.height / 2 - tooltipHeight / 2;
    arrowPosition = 'right';
  }

  // Ensure tooltip doesn't go off screen
  if (tooltipTop < 10) {
    tooltipTop = cutout.top + cutout.height + 12;
    tooltipLeft = cutout.left + cutout.width / 2 - tooltipWidth / 2;
    arrowPosition = 'top';
  }
  if (tooltipTop + tooltipHeight > viewportHeight - 10) {
    tooltipTop = cutout.top - tooltipHeight - 12;
    tooltipLeft = cutout.left + cutout.width / 2 - tooltipWidth / 2;
    arrowPosition = 'bottom';
  }
  if (tooltipLeft < 10) {
    tooltipLeft = 10;
  }
  if (tooltipLeft + tooltipWidth > viewportWidth - 10) {
    tooltipLeft = viewportWidth - tooltipWidth - 10;
  }

  const tooltip = {
    top: tooltipTop,
    left: tooltipLeft,
    arrowPosition,
  };

  return createPortal(
    <>
      {/* SVG Mask for rounded cutout */}
      <svg
        className="fixed inset-0 pointer-events-none z-[9996]"
        style={{ width: '100%', height: '100%' }}
      >
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x={cutout.left}
              y={cutout.top}
              width={cutout.width}
              height={cutout.height}
              rx="8"
              ry="8"
              fill="black"
            />
          </mask>
        </defs>
      </svg>

      {/* Overlay with rounded cutout using SVG mask */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close spotlight guide"
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 z-[9997] pointer-events-auto"
        style={{
          opacity: isVisible ? 1 : 0,
          mask: 'url(#spotlight-mask)',
          WebkitMask: 'url(#spotlight-mask)',
        }}
        onClick={handleDismiss}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleDismiss();
          }
        }}
      />

      {/* Highlight border around button */}
      <div
        className="fixed z-[9998] pointer-events-none rounded-lg border-2 border-white/20"
        style={{
          top: cutout.top,
          left: cutout.left,
          width: cutout.width,
          height: cutout.height,
        }}
      />

      {/* Pulsing glow around button with rounded corners */}
      <div
        className="fixed z-[9998] pointer-events-none rounded-lg"
        style={{
          top: cutout.top,
          left: cutout.left,
          width: cutout.width,
          height: cutout.height,
          animation: 'spotlight-pulse 2s ease-in-out infinite',
        }}
      />

      {/* Tooltip */}
      <div
        className="fixed z-[9998] bg-white rounded-lg shadow-lg p-3 w-[220px] animate-in slide-in-from-top-2 fade-in duration-300"
        style={{
          top: tooltip.top,
          left: tooltip.left,
        }}
      >
        {/* Dynamic arrow based on position */}
        {tooltip.arrowPosition === 'left' && (
          <div
            className="absolute top-1/2 -translate-y-1/2 -left-2 w-0 h-0"
            style={{
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: '8px solid white',
            }}
          />
        )}
        {tooltip.arrowPosition === 'right' && (
          <div
            className="absolute top-1/2 -translate-y-1/2 -right-2 w-0 h-0"
            style={{
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: '8px solid white',
            }}
          />
        )}
        {tooltip.arrowPosition === 'top' && (
          <div
            className="absolute left-1/2 -translate-x-1/2 -top-2 w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '8px solid white',
            }}
          />
        )}
        {tooltip.arrowPosition === 'bottom' && (
          <div
            className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid white',
            }}
          />
        )}

        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 h-6 w-6 rounded-full bg-primary-50 flex items-center justify-center">
            <i className="ti ti-bulb text-sm text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-900 mb-1">Quick tip!</p>
            <p className="text-[11px] text-gray-600 leading-relaxed">
              {message}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close tooltip"
            className="flex-shrink-0 h-5 w-5 rounded hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <i className="ti ti-x text-xs text-gray-500" />
          </button>
        </div>
      </div>

      {/* Pulse animation keyframes */}
      <style>
        {`
          @keyframes spotlight-pulse {
            0%, 100% {
              box-shadow: 0 0 0 3px rgba(236, 72, 153, 0.3),
                          0 0 12px 2px rgba(236, 72, 153, 0.2);
            }
            50% {
              box-shadow: 0 0 0 6px rgba(236, 72, 153, 0.5),
                          0 0 20px 4px rgba(236, 72, 153, 0.3);
            }
          }
        `}
      </style>
    </>,
    document.body
  );
};
